import type React from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { type Body, type Meta, Uppy, type UppyFile } from '@uppy/core'
import type { AwsS3Part } from '@uppy/aws-s3'
import AwsS3Multipart from '@uppy/aws-s3'
import '@uppy/core/css/style.min.css'
import '@uppy/dashboard/css/style.min.css'
import { Environment } from '../environment.ts'
import type { EItemType, NegativeControl, Sample } from '../Model/index.ts'
import type { IFileUploadLocation } from '../Model/File/IFileUploadLocation.ts'
import { useDispatch } from 'react-redux'
import { addMessage } from '../Flux/Slice/Application/ApplicationSlice.ts'
import { errorMessage, infoMessage } from '../Model/Message/IMessage.ts'
import { formatFileNames, prevalidateFiles } from '../View/Page/OrderManagementPage/FileHandling/PreValidator.ts'
import type { SimpleFile } from './FileMap.ts'
import { FileMap } from './FileMap.ts'
import { Subject } from 'rxjs'
import Actions from '../Flux/index.ts'
import { orderSlice } from '../Flux/Slice/Order/OrderSlice.ts'
import type { AwsS3UploadParameters } from '@uppy/aws-s3'
import { getPairName, sampleIsForward } from '../Util/SampleFileUtils.js'
import type { DetectedSequencingPlatform } from '../Util/sequencingPlatformDetection.ts'
import { detectSequencingPlatforms } from '../Util/sequencingPlatformDetection.ts'
import { tracingAxios } from '../Repository/Sentry.js'
import { trackUploadFileCount, trackUploadFileSize, trackUploadOutcome } from '../Services/Telemetry/index.ts'

export type UploadMeta = Meta & {
  orderId: string
  poolId: string
  itemType: EItemType
  pendingId?: string
  isMerged: boolean
  isTrimmed: boolean
  /** What the file's own read headers said, sniffed at selection time. */
  detectedSequencingPlatform?: DetectedSequencingPlatform
  /** The sample missing its R2 this file is uploaded for; it is attached to that sample instead of paired. */
  attachReverseTo?: string
}

type ContentPayload = Omit<Sample, 'id'> | Omit<NegativeControl, 'id'>

/**
 * `UNKNOWN` is a verdict meaning the reads did not say, not a platform. It must not be persisted —
 * the stored column holds a real platform or nothing — so it drops out here rather than downstream.
 */
const storablePlatform = (detected: DetectedSequencingPlatform | undefined) =>
  detected && detected !== 'UNKNOWN' ? detected : undefined

export type UppyFileWithMeta = UppyFile<UploadMeta, Body>

/**
 * A file's own meta, with anything it is missing taken from the globally selected pool.
 *
 * The `files-added` snapshot predates the `file-added` handler that patches that global meta onto a
 * file dropped without one (the dashboard's own drag & drop), so reading the snapshot directly sees
 * `undefined` for those fields. That is how a merged-samples order broke: a merged sample carries no
 * read designation by definition, `isMerged` read as undefined, and pre-validation rejected every file
 * as `no-read-designation`. The pool's own file input sets the meta before it validates, which is why
 * only drag & drop was affected.
 *
 * Only fills gaps — a file that carries its own value always keeps it.
 */
export const mergeUploadMeta = (fileMeta: Partial<UploadMeta> | undefined, fallback: UploadMeta | null): UploadMeta =>
  ({
    ...fallback,
    ...fileMeta,
    isMerged: fileMeta?.isMerged ?? fallback?.isMerged,
    isTrimmed: fileMeta?.isTrimmed ?? fallback?.isTrimmed,
    itemType: fileMeta?.itemType ?? fallback?.itemType
  }) as UploadMeta

/**
 * Pre-validates a `files-added` batch against what its target pools already hold. The pool comes from
 * the resolved meta, not the snapshot: a dashboard drop carries no `poolId` there yet.
 */
export const prevalidateBatch = <T extends { name?: string | null; data?: unknown; meta?: Partial<UploadMeta> }>(
  files: T[],
  selectedPool: UploadMeta | null,
  knownFileNames: (poolId: string) => string[]
) => {
  /* layered rather than replaced: uppy's meta also carries its own InternalMetadata */
  const resolved = files.map((file) => ({ ...file, meta: { ...file.meta, ...mergeUploadMeta(file.meta, selectedPool) } }))
  const pools = new Set(resolved.map((f) => f.meta.poolId).filter((poolId): poolId is string => !!poolId))
  return prevalidateFiles(resolved, [...pools].flatMap(knownFileNames))
}

interface UppyContextType {
  uppy: Uppy<UploadMeta, Body> | null
  meta: UploadMeta | null
  setMeta: (meta: UploadMeta) => void
  files: FileMap | null
  poolIsLoading: (poolId?: string) => boolean
  /** File names the pool already knows about: uploaded to S3 plus still queued in the uploader. */
  knownFileNames: (poolId: string) => string[]
}

type SkippedFile = SimpleFile & {
  itemType: null
  wasSkipped: true
  isMerged: boolean
  isTrimmed: boolean
  detectedSequencingPlatform?: DetectedSequencingPlatform
}

type NewFile = SimpleFile & {
  itemType: EItemType
  wasSkipped: false
  isMerged: boolean
  isTrimmed: boolean
  detectedSequencingPlatform?: DetectedSequencingPlatform
}

type MergeFile = SkippedFile | NewFile

/**
 * The R1/R2 partner of a freshly uploaded file: one still waiting in this batch first, else one the pool already
 * holds. The second is what lets a lone R2 dropped later complete the R1 sample left missing its R2 (by a
 * merged-to-paired switch, or a first upload without it), where it used to wait forever for a partner.
 */
export const findMate = <T extends SimpleFile>(
  fileName: string,
  buffered: readonly T[],
  uploaded: readonly SimpleFile[]
): { mate: T; buffered: true } | { mate: SimpleFile; buffered: false } | null => {
  const expected = getPairName(fileName)
  if (!expected) return null
  const waiting = buffered.find((f) => f.fileName === expected)
  if (waiting) return { mate: waiting, buffered: true }
  const known = uploaded.find((f) => f.fileName === expected && !!f.id)
  return known ? { mate: known, buffered: false } : null
}

const UppyContext = createContext<UppyContextType>({
  uppy: null,
  meta: null,
  files: null,
  setMeta: () => {},
  poolIsLoading: () => true,
  knownFileNames: () => []
})

/** How long upload failures are collected before they are reported as one message. */
const FAILURE_REPORT_DELAY_MS = 2000

/** Why a file never made it into the uploader — aggregated into one message per batch, see `flushSkips`. */
type SkipKind = 'already-uploaded' | 'still-uploading' | 'retried' | 'pool-loading'

const noSkips = (): Record<SkipKind, string[]> => ({
  'already-uploaded': [],
  'still-uploading': [],
  retried: [],
  'pool-loading': []
})

const skipSummary = (skips: Record<SkipKind, string[]>, acceptedCount: number): string | undefined => {
  const lines: string[] = []
  const { 'already-uploaded': done, 'still-uploading': inFlight, retried } = skips
  if (done.length > 0) {
    lines.push(
      acceptedCount === 0 && inFlight.length === 0 && retried.length === 0
        ? `All ${done.length} selected files are already uploaded to this pool — nothing left to upload.`
        : `${done.length} of the selected files are already uploaded to this pool and were skipped.`
    )
  }
  if (inFlight.length > 0) {
    lines.push(`${inFlight.length} of the selected files are still uploading — they were not added a second time.`)
  }
  if (retried.length > 0) {
    lines.push(`Retrying ${retried.length} file${retried.length === 1 ? '' : 's'} that did not finish earlier.`)
  }
  return lines.length > 0 ? lines.join('\n') : undefined
}

export const useUppy = () => {
  const context = useContext(UppyContext)
  if (!context) throw new Error('UppyContext not found')
  return context
}

export const UppyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [meta, setMeta] = useState<UploadMeta | null>(null)
  const metaRef = useRef<UploadMeta | null>(null)
  const dispatch = useDispatch()
  const dispatchRef = useRef(dispatch)

  const [files, setFiles] = useState<FileMap>(new FileMap())
  const filesRef = useRef<FileMap | null>(null)

  // The order/pool the uploader was last pointed at, so we can detect a real context switch (vs. an
  // itemType change) and clear stale completed files from Uppy — see the meta effect below.
  const uploadContextRef = useRef<string | null>(null)

  const mergeQueue$ = useRef(new Subject<MergeFile>()).current

  // Files rejected by onBeforeFileAdded, collected per addFiles() batch and reported as ONE message.
  // Reporting per file meant 1300 selected files produced ~1300 snackbar messages, of which only the
  // last one is ever rendered — so the user effectively got no feedback at all.
  const skipsRef = useRef<Record<SkipKind, string[]>>(noSkips())
  const skipFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // How many files the last batch actually accepted, so the summary can say "nothing left to upload".
  const acceptedInBatchRef = useRef(0)
  const uppyRef = useRef<Uppy<UploadMeta, Body> | null>(null)

  const flushSkips = () => {
    skipFlushRef.current = null
    const skips = skipsRef.current
    skipsRef.current = noSkips()
    console.info('upload skips', skips)
    const summary = skipSummary(skips, acceptedInBatchRef.current)
    if (summary) {
      dispatchRef.current(addMessage(infoMessage(summary)))
    }
    if (skips['pool-loading'].length > 0) {
      dispatchRef.current(
        addMessage(
          errorMessage(
            `${skips['pool-loading'].length} files could not be added because the pool was still loading. ` +
              'Please wait for "Preparing pool…" to disappear and select them again.'
          )
        )
      )
    }
  }

  const failedRef = useRef<string[]>([])
  const failureFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flushFailures = () => {
    failureFlushRef.current = null
    const failed = failedRef.current
    failedRef.current = []
    if (failed.length === 0) return
    dispatchRef.current(
      addMessage(
        errorMessage(
          `${failed.length} file${failed.length === 1 ? '' : 's'} failed to upload: ${formatFileNames(failed)}. ` +
            'Select them again to retry — files that already made it are skipped automatically.'
        )
      )
    )
  }

  const noteFailure = (fileName: string) => {
    failedRef.current.push(fileName)
    /* uploads fail spread over time, so collect for a moment before speaking up */
    if (failureFlushRef.current === null) failureFlushRef.current = setTimeout(flushFailures, FAILURE_REPORT_DELAY_MS)
  }

  const noteSkip = (kind: SkipKind, fileName: string) => {
    skipsRef.current[kind].push(fileName)
    // Uppy adds a whole batch synchronously and emits `files-added` before yielding, so a macrotask
    // is enough to see the complete picture (skipped + accepted) for the batch.
    if (skipFlushRef.current === null) skipFlushRef.current = setTimeout(flushSkips, 0)
  }

  function hasUploadMeta(file: UppyFile<Meta, Body>): file is UppyFileWithMeta {
    const meta = file.meta

    return (
      typeof meta.orderId === 'string' &&
      typeof meta.poolId === 'string' &&
      typeof meta.itemType === 'string' &&
      typeof meta.isMerged === 'boolean' &&
      typeof meta.isTrimmed === 'boolean'
    )
  }

  useEffect(() => {
    const buffer: Record<string, MergeFile[]> = {}
    const sub = mergeQueue$.subscribe((file) => {
      if (!file.isMerged) {
        const key = `${file.orderId}:${file.poolId}`
        const current = buffer[key] || []
        if (file.wasSkipped) {
          buffer[key] = [...current, file]
          return
        }

        const poolFiles = filesRef.current?.byPool[file.poolId]
        const found = findMate(file.fileName, current, poolFiles && poolFiles !== 'LOADING' ? poolFiles : [])

        if (found) {
          const match = found.mate
          const isForward = sampleIsForward(file.fileName)
          const forward = isForward ? file : match
          const reverse = isForward ? match : file
          const forwardPlatform = isForward || !found.buffered ? file.detectedSequencingPlatform : found.mate.detectedSequencingPlatform

          const sample: ContentPayload = {
            itemType: file.itemType,
            forwardFileId: forward.id,
            reverseFileId: reverse.id,
            isTrimmed: file.isTrimmed,
            isMerged: file.isMerged,
            detectedSequencingPlatform: storablePlatform(forwardPlatform),
            version: null
          }
          dispatch(
            Actions[orderSlice.name].postContentRequest({
              orderId: file.orderId,
              poolId: file.poolId,
              sample
            })
          )
          buffer[key] = found.buffered ? current.filter((f) => f !== match) : current
        } else {
          buffer[key] = [...current, file]
        }
      } else {
        /* handle files which do not need merging */
        if (!file.itemType) {
          dispatch(addMessage(errorMessage('Cannot merge files without item type')))
        } else {
          const sample: ContentPayload = {
            itemType: file.itemType,
            forwardFileId: file.id,
            isTrimmed: file.isTrimmed,
            isMerged: file.isMerged,
            detectedSequencingPlatform: storablePlatform(file.detectedSequencingPlatform),
            version: null
          }
          dispatch(
            Actions[orderSlice.name].postContentRequest({
              orderId: file.orderId,
              poolId: file.poolId,
              sample
            })
          )
        }
      }
    })
    return () => sub.unsubscribe()
  }, [mergeQueue$])

  // Sync these latest-value refs DURING render, not in a useEffect. onBeforeFileAdded (and other
  // Uppy callbacks) read them synchronously, and effects run only after commit+paint — so an effect
  // update lets a fast file-add (or a Cypress step) observe a stale ref, e.g. a pool that React
  // state already shows as loaded but the ref still reports as 'LOADING' → a bogus
  // "skipping file for safety". Assigning here keeps the refs in lockstep with the render.
  filesRef.current = files
  dispatchRef.current = dispatch
  metaRef.current = meta

  const knownFileNames = (poolId: string): string[] => {
    const uploaded = filesRef.current?.fileNamesInPool(poolId) ?? []
    const queued = uppyRef.current
      ?.getFiles()
      .filter((f) => f.meta.poolId === poolId)
      .map((f) => f.name)
      .filter((name): name is string => !!name)
    return [...new Set([...uploaded, ...(queued ?? [])])]
  }

  const [uppy] = useState(
    () =>
      new Uppy<UploadMeta, Body>({
        autoProceed: true,
        restrictions: {
          allowedFileTypes: ['.gz']
        },
        onBeforeFileAdded: (currentFile: UppyFileWithMeta, files) => {
          const currentMeta = metaRef.current
          const currentFiles = filesRef.current
          if (!currentFiles || !currentFile.name) return true
          // Check the pool the file actually targets. Files added via the pool's FileInput already
          // carry their own orderId/poolId; only dashboard drag-drops arrive without meta and fall
          // back to the globally selected pool (their meta is applied in the file-added handler).
          // This keeps the loading/duplicate decision aligned with the per-pool loading guard,
          // instead of consulting a globally-selected pool that may differ or still be loading.
          const orderId = currentFile.meta?.orderId || currentMeta?.orderId
          const poolId = currentFile.meta?.poolId || currentMeta?.poolId
          if (!orderId || !poolId) return true
          const duplicate = currentFiles.findDuplicate(orderId, poolId, currentFile.name)
          if (duplicate === true) {
            console.error('unexpected case: this should only rarely happen, as this means stuff is loading')
            noteSkip('pool-loading', currentFile.name)
            return false
          }
          if (duplicate && currentFile.meta.attachReverseTo) {
            dispatchRef.current(
              Actions[orderSlice.name].attachReverseFileRequest({
                orderId,
                poolId,
                sampleId: currentFile.meta.attachReverseTo,
                reverseFileId: duplicate.id
              })
            )
            return false
          }
          if (duplicate) {
            mergeQueue$.next({
              ...duplicate,
              wasSkipped: true,
              itemType: null,
              isMerged: currentFile.meta.isMerged,
              isTrimmed: currentFile.meta.isTrimmed
            })
            noteSkip('already-uploaded', currentFile.name)
            return false
          }
          // Uppy dedups on an id derived from name/size/type, so an entry left over from an earlier
          // attempt (failed, or "complete" but never verified server-side) silently swallowed the
          // re-add — the retry of exactly those files looked like nothing happened at all. The file is
          // NOT in the pool (findDuplicate said so), so the leftover entry is stale: drop it and let
          // the retry through. Uppy re-reads its state after this callback, so removing here is safe.
          const stale = files[currentFile.id]
          if (stale) {
            // A failed entry also has uploadStarted set and uploadComplete false — it is the very thing
            // we want to retry, so it must not count as in flight.
            const failed = !!stale.error
            const inFlight = !failed && stale.progress?.uploadStarted != null && stale.progress?.uploadComplete !== true
            if (inFlight) {
              /* removing it would abort a live upload */
              noteSkip('still-uploading', currentFile.name)
              return false
            }
            uppyRef.current?.removeFile(stale.id)
            noteSkip('retried', currentFile.name)
          }
          return true
        }
      })
  )
  uppyRef.current = uppy

  // update already present files when meta changes
  // Keyed on the primitive order/pool ids, NOT the `meta` object. `meta` gets a fresh identity on
  // every setMeta (and the caller re-derives it on each Redux update, plus on itemType toggles), so
  // depending on the object would re-run this on every store tick and flip the pool back into
  // 'LOADING' repeatedly — a moving target that races file adds against the loading guard. We only
  // want to (re)load when the actual order/pool changes.
  const metaOrderId = meta?.orderId
  const metaPoolId = meta?.poolId
  useEffect(() => {
    if (!metaOrderId || !metaPoolId) return

    // On a real order/pool switch, drop finished files from the uploader. Uppy dedups by an id
    // derived from name/size/type, so leftover completed files from the previous context would
    // silently block re-adding same-named files to the new one (e.g. re-uploading after deleting a
    // pool and creating a new one). In-pool duplicate detection stays intact — it's driven by
    // FileMap (findDuplicate), not Uppy's file list. In-flight uploads are left untouched.
    const contextKey = `${metaOrderId}:${metaPoolId}`
    if (uploadContextRef.current !== null && uploadContextRef.current !== contextKey) {
      uppy.getFiles().forEach((f) => {
        if (f.progress?.uploadComplete) uppy.removeFile(f.id)
      })
    }
    uploadContextRef.current = contextKey

    setFiles((files) => files.startLoading(metaPoolId))

    const fetchCompleted = async () => {
      try {
        const res = await tracingAxios.get<IFileUploadLocation[]>(
          `${Environment.API_URL}/api/orders/${metaOrderId}/pools/${metaPoolId}/file-urls/completed`,
          {
            withCredentials: true
          }
        )
        setFiles((files) => files.poolLoaded(metaOrderId, metaPoolId, res.data))
      } catch (err) {
        console.error('Failed to fetch completed files:', err)
        // Never leave the pool stuck in 'LOADING': that permanently disables the inputs and makes
        // onBeforeFileAdded reject every file ("skipping file for safety"). Since this effect now
        // fetches once per pool (no more re-fetch-on-every-render masking a failure), a single
        // failed request would otherwise wedge the pool forever. Treat it as "no completed files
        // known" so the pool stays usable; the backend still guards against true duplicates.
        setFiles((files) => files.poolLoaded(metaOrderId, metaPoolId, []))
      }
    }
    fetchCompleted().then(() => console.info('Fetched completed files'))
  }, [metaOrderId, metaPoolId])

  useEffect(() => {
    const handler = (file: UppyFileWithMeta | undefined) => {
      if (!file) return
      const currentMeta = metaRef.current
      if (!file.meta.orderId && (!currentMeta || !currentMeta.orderId || !currentMeta.poolId)) {
        uppy.removeFile(file.id)
        dispatchRef.current(addMessage(errorMessage('Cannot upload file, navigate to a valid pool first')))
      }

      if (!file.meta.orderId) {
        uppy.setFileMeta(file.id, {
          ...file.meta,
          ...currentMeta
        })
      } else {
        console.info('file added with meta, skipping global meta')
      }
    }
    uppy.on('file-added', handler)
    return () => {
      uppy.off('file-added', handler)
    }
  }, [uppy])

  // Safety net for files that arrive without going through SamplePoolWidget's pre-validation (the
  // dashboard's own drag & drop). Only the files that are actually unusable are dropped — bouncing the
  // whole batch over one bad name was brutal at 1300-file scale — and pairing is judged against what
  // the pool already has, so re-uploading one half of a pair is fine.
  useEffect(() => {
    // Sniffing is async, so a file that turns out to be rejectable may have started uploading by the
    // time we remove it. That only ever delays a rejection the user would have got anyway, whereas
    // running without a verdict would reject every ONT file for a missing R1/R2 designation.
    const detectMissing = async (files: UppyFile<UploadMeta, Body>[]) => {
      const undetected = files.filter((file) => !file.meta.detectedSequencingPlatform && file.data instanceof Blob)
      if (undetected.length === 0) return
      const platforms = await detectSequencingPlatforms(undetected.map((file) => file.data as Blob))
      undetected.forEach((file) => {
        const platform = platforms.get(file.data as Blob)
        const live = uppy.getFile(file.id)
        if (platform && live) uppy.setFileMeta(file.id, { ...live.meta, detectedSequencingPlatform: platform })
      })
    }

    const validateBatch = async (files: UppyFile<UploadMeta, Body>[]) => {
      acceptedInBatchRef.current = files.length
      if (files.length === 0) return
      await detectMissing(files)

      const live = files.map((file) => uppy.getFile(file.id) ?? file)
      const { accepted, rejected, messages } = prevalidateBatch(live, metaRef.current, knownFileNames)

      if (rejected.length > 0) {
        dispatchRef.current(addMessage(errorMessage(messages.join('\n'))))
        rejected.forEach(({ file }) => uppy.removeFile(file.id))
      }

      acceptedInBatchRef.current = accepted.length
      trackUploadFileCount(accepted.length)
      // `files` is the snapshot Uppy captured when the batch arrived, taken BEFORE the `file-added`
      // handler above patches the global meta onto files dropped without one (the dashboard's own
      // drag & drop). Reading `f.meta.itemType` off that snapshot reported `undefined` for exactly
      // those files — a third of all upload samples. Re-read the live file, then fall back to the
      // meta that handler would have applied.
      accepted.forEach((f) =>
        trackUploadFileSize(f.size ?? 0, uppy.getFile(f.id)?.meta.itemType ?? metaRef.current?.itemType)
      )
    }
    const handler = (files: UppyFile<UploadMeta, Body>[]) => void validateBatch(files)
    uppy.on('files-added', handler)
    return () => {
      uppy.off('files-added', handler)
    }
  }, [uppy])

  useEffect(() => {
    const handler = (file: UppyFileWithMeta | undefined) => {
      if (!file) return
      const { orderId, poolId, itemType, pendingId, isMerged, isTrimmed, detectedSequencingPlatform } = file.meta
      trackUploadOutcome('success', itemType)
      const fileName = file.name

      if (!fileName) {
        console.error('file has no name, skipping')
        return
      }

      if (!pendingId) {
        console.error('file has no pendingId, skipping')
        return
      }

      // Single-PUT uploads (files under the multipart threshold) have no server completion round-trip,
      // so the backend never marks them verified — which makes them invisible to duplicate "recover"
      // (the /file-urls/completed fetch keys on verifiedAt). Verify here on success. Idempotent, so it's
      // a cheap no-op for multipart uploads (already verified by /s3/multipart/complete). Fire-and-forget.
      tracingAxios
        .post(`${Environment.API_URL}/api/orders/${orderId}/pools/${poolId}/file-urls/${pendingId}/verify`, {}, { withCredentials: true })
        .catch((err) => console.error('Failed to verify upload', err))

      setFiles((files) => files.fileLoaded(orderId, poolId, fileName, pendingId))
      if (file.meta.attachReverseTo) {
        dispatchRef.current(
          Actions[orderSlice.name].attachReverseFileRequest({ orderId, poolId, sampleId: file.meta.attachReverseTo, reverseFileId: pendingId })
        )
        return
      }
      mergeQueue$.next({
        id: pendingId,
        fileName: fileName,
        poolId: poolId,
        orderId: orderId,
        itemType: itemType,
        wasSkipped: false,
        isMerged,
        isTrimmed,
        detectedSequencingPlatform
      })
    }
    uppy.on('upload-success', handler)
    return () => {
      uppy.off('upload-success', handler)
    }
  }, [meta])

  // Failed uploads used to surface only inside the dashboard drawer, which is closed most of the time
  // (uploads run with autoProceed). A partly-failed batch therefore looked successful and the user only
  // found out at order submission, with no idea which files to fix. Report failures as they happen,
  // batched, so a flaky connection during a 1300-file upload produces one message and not 1300.
  useEffect(() => {
    const handler = (file: UppyFileWithMeta | undefined) => {
      trackUploadOutcome('error', file?.meta.itemType)
      if (file?.name) noteFailure(file.name)
    }
    uppy.on('upload-error', handler)
    return () => {
      uppy.off('upload-error', handler)
    }
  }, [uppy])

  useEffect(() => {
    const API_BASE = Environment.API_URL

    uppy.use(AwsS3Multipart, {
      async getUploadParameters(file): Promise<AwsS3UploadParameters> {
        if (!hasUploadMeta(file)) {
          throw new Error('Missing upload metadata')
        }
        const { orderId, poolId, itemType } = file.meta

        const registerRes = await tracingAxios.post<IFileUploadLocation[]>(
          `${API_BASE}/api/orders/${orderId}/pools/${poolId}/file-urls`,
          {
            files: [{ fileName: file.name, encodedMd5: '', contentType: itemType }]
          },
          { withCredentials: true }
        )

        const [upload] = registerRes.data
        // The backend answers with nothing for a file it already holds verified, so this is not a
        // "server broken" case — it means the pool moved on under us. Say so instead of dying on
        // `undefined.id` with a stack trace the user can do nothing with.
        if (!upload) {
          throw new Error(`${file.name} is already uploaded to this pool — reload the page to see it.`)
        }

        uppy.setFileMeta(file.id, { ...file.meta, pendingId: upload.id })

        return {
          method: 'PUT',
          url: upload.location
        }
      },
      async createMultipartUpload(file) {
        if (!hasUploadMeta(file)) {
          throw new Error('Missing upload metadata')
        }
        const { orderId, poolId, itemType } = file.meta

        const registerRes = await tracingAxios.post(
          `${API_BASE}/api/orders/${orderId}/pools/${poolId}/file-urls`,
          {
            files: [{ fileName: file.name, encodedMd5: '', contentType: itemType }]
          },
          { withCredentials: true }
        )

        const [upload] = registerRes.data
        if (!upload) {
          throw new Error(`${file.name} is already uploaded to this pool — reload the page to see it.`)
        }

        const startRes = await tracingAxios.post(
          `${API_BASE}/s3/multipart/start`,
          {
            uploadId: upload.id,
            contentType: file.type
          },
          { withCredentials: true }
        )

        uppy.setFileMeta(file.id, { ...file.meta, pendingId: upload.id })

        return {
          uploadId: startRes.data.uploadId,
          key: startRes.data.key,
          meta: {
            pendingId: upload.id
          }
        }
      },

      async listParts(file, { uploadId, key }) {
        const res = await tracingAxios.get(`${API_BASE}/s3/multipart/${uploadId}/parts`, {
          params: {
            key,
            pendingId: file.meta.pendingId
          },
          withCredentials: true
        })
        return res.data as AwsS3Part[]
      },

      async abortMultipartUpload(file, { uploadId, key }) {
        await tracingAxios.delete(`${API_BASE}/s3/multipart/${uploadId}`, {
          params: {
            key,
            pendingId: file.meta.pendingId
          },
          withCredentials: true
        })
      },

      async completeMultipartUpload(file, { uploadId, key, parts }) {
        const res = await tracingAxios.post(
          `${API_BASE}/s3/multipart/complete`,
          {
            pendingId: file.meta.pendingId,
            uploadId,
            key,
            parts
          },
          { withCredentials: true }
        )
        return { location: res.data.location }
      },

      async signPart(file, { uploadId, partNumber }) {
        const res = await tracingAxios.get(`${API_BASE}/s3/multipart/${uploadId}/${partNumber}`, {
          params: {
            pendingId: file.meta.pendingId
          },
          withCredentials: true
        })
        return { url: res.data.url }
      }
    })

    return () => uppy.destroy()
  }, [])

  return (
    <UppyContext.Provider
      value={{
        uppy: uppy,
        meta: meta,
        setMeta: (meta) => {
          setMeta(meta)
        },
        files: files,
        poolIsLoading: (poolId?: string) => files.poolIsLoading(poolId),
        knownFileNames: knownFileNames
      }}
    >
      {children}
    </UppyContext.Provider>
  )
}
